import {
  Box,
  Button,
  Flex,
  IconButton,
  Spinner,
  Step,
  StepIndicator,
  StepNumber,
  Stepper,
  StepSeparator,
  StepStatus,
  StepTitle,
  Text,
} from "@chakra-ui/react";
import ModalDrawer from "@components/ModalDrawer";
// import { IoClose } from "react-icons/io5";
import { XIcon as IoClose } from "@phosphor-icons/react";
import { FaCheck } from "react-icons/fa6";

import { IoReloadOutline } from "react-icons/io5";

import BaseImage from "@components/BaseImage";
import TransactionModal from "@components/TransactionModal";
import {
  GAIN_DEPOSIT_VAULT,
  LSTIconMapping,
  TXN_TYPES,
} from "@constants/common";
import { CONFIRM_TRANSACTIONS } from "@constants/analytics";
import useDappChain from "@hooks/useDappChain";
import { TxnErrorType } from "@services/wagmi";
import { TransactionReceipt } from "viem";
import { trackEvent } from "@lib/utils";
import { ReactNode } from "react";
import UnstakeConfirmation from "./UnstakeConfirmation";
import Link from "next/link";
import useHgEthExchangeRate from "@hooks/useHgEthExchangeRate";
import useAgEthExchangeRate from "@hooks/useAgEthExchangeRate";
import useEthConversion from "@hooks/useEthConversion";

export enum TransactionType {
  APPROVAL = "APPROVAL",
  DEPOSIT = "DEPOSIT",
}

interface DepositModalProps {
  txModalTitle?: ReactNode;
  transaction: {
    from: string;
    to: string;
    amountIn: string;
    amountOut: string;
    interactionPool?: string;
  };
  complete: boolean;
  inProgress: boolean;
  type: TransactionType;
  error: {
    approval: TxnErrorType | null;
    deposit: TxnErrorType | null;
  };
  visible: boolean;
  status: TransactionType;
  flags: {
    isVault: boolean;
    isUnstake: boolean;
    isYieldVault?: boolean;
    isStableVault?: boolean;
    isCryptaniumVault?: boolean;
    isClaim?: boolean;
    isWrap?: boolean;
  };
  closeAlert: () => void;
  retryTransaction?: () => void;
  handleConfirm?: () => void;
  receipt: TransactionReceipt | null;
  nextAction?: () => void;
  isStakeWithHgEth?: boolean;
}
const TRANSACTIONS_KEYS = {
  [TXN_TYPES.VAULT_DEPOSIT]: "Deposit",
  [TXN_TYPES.YIELD_VAULT_DEPOSIT]: "Deposit",
  [TXN_TYPES.STABLE_VAULT_DEPOSIT]: "Deposit",
  [TXN_TYPES.VAULT_WITHDRAW]: "Withdraw",
  [TXN_TYPES.YIELD_VAULT_WITHDRAW]: "Withdraw",
  [TXN_TYPES.STABLE_VAULT_WITHDRAW]: "Withdraw",
  [TXN_TYPES.STABLE_VAULT_CLAIM]: "Claim",
  [TXN_TYPES.STAKE]: "Restake",
  [TXN_TYPES.UNSTAKE]: "Unstake",
  [TXN_TYPES.CRYPTANIUM_VAULT_DEPOSIT]: "Deposit",
  [TXN_TYPES.CRYPTANIUM_VAULT_WITHDRAW]: "Withdraw",
  [TXN_TYPES.CRYPTANIUM_VAULT_CLAIM]: "Claim",
  [TXN_TYPES.WRAP]: "wrap",
  [TXN_TYPES.UNWRAP]: "unwrap",
};

const DepositModalV2 = (props: DepositModalProps) => {
  const {
    error,
    handleConfirm,
    receipt,
    flags,
    transaction,
    nextAction,
    txModalTitle,
    isStakeWithHgEth,
  } = props;
  const { chain } = useDappChain();
  const { toERC20 } = useHgEthExchangeRate();
  const { valueInEth: hgEthRateInEth } = useEthConversion(toERC20);
  const { toERC20: agEthToERC20 } = useAgEthExchangeRate();
  const { valueInEth: agEthRateInEth } = useEthConversion(agEthToERC20);
  // The Gain cross-sell deposit modal is env-toggled to a single vault; show
  // that vault's token, exchange rate, and vault link.
  const isAgEthGain = GAIN_DEPOSIT_VAULT === "ageth";
  const gainVaultToken = isAgEthGain ? "agETH" : "hgETH";
  const gainVaultRate = isAgEthGain ? agEthRateInEth : hgEthRateInEth;
  const gainVaultLink = isAgEthGain
    ? "https://gain.kerneldao.com/vaults/airdrop-vault"
    : "https://gain.kerneldao.com/vaults/growth-vault";

  const transactionType = (() => {
    if (flags.isVault) {
      return flags.isUnstake
        ? flags.isClaim && flags.isStableVault
          ? TXN_TYPES.STABLE_VAULT_CLAIM
          : flags.isClaim && flags.isCryptaniumVault
          ? TXN_TYPES.CRYPTANIUM_VAULT_CLAIM
          : flags.isStableVault
          ? TXN_TYPES.STABLE_VAULT_WITHDRAW
          : flags.isCryptaniumVault
          ? TXN_TYPES.CRYPTANIUM_VAULT_WITHDRAW
          : flags.isYieldVault
          ? TXN_TYPES.YIELD_VAULT_WITHDRAW
          : TXN_TYPES.VAULT_WITHDRAW
        : flags.isStableVault
        ? TXN_TYPES.STABLE_VAULT_DEPOSIT
        : flags.isCryptaniumVault
        ? TXN_TYPES.CRYPTANIUM_VAULT_DEPOSIT
        : flags.isYieldVault
        ? TXN_TYPES.YIELD_VAULT_DEPOSIT
        : TXN_TYPES.VAULT_DEPOSIT;
    }
    // Handle Wrap/Unwrap transactions
    if (flags.isWrap !== undefined) {
      return flags.isWrap ? TXN_TYPES.WRAP : TXN_TYPES.UNWRAP;
    }
    return flags.isUnstake ? TXN_TYPES.UNSTAKE : TXN_TYPES.STAKE;
  })();

  const isUnstakeTx = transactionType === TXN_TYPES.UNSTAKE && nextAction;
  const steps = [
    { title: `Approve ${transaction.from} in wallet` },
    { title: `Confirm ${TRANSACTIONS_KEYS[transactionType]}` },
  ];

  const closeModal = () => {
    props.closeAlert();
  };

  const onConfirm = () => {
    trackEvent(CONFIRM_TRANSACTIONS, {
      transaction_type: TRANSACTIONS_KEYS[transactionType],
      from_token: transaction.from,
      to_token: transaction.to,
      amount_in: transaction.amountIn,
      amount_out: transaction.amountOut,
    });
    handleConfirm?.();
  };

  if (props.complete) {
    return (
      <TransactionModal
        isOpen={true}
        hash={receipt?.transactionHash || ""}
        error={error.deposit?.promptError || ""}
        transactionType={transactionType}
        isTxnProcessing={false}
        hasUserDenied={props.error.deposit?.hasUserDenied || false}
        closeAlert={() => closeModal()}
        handleTxView={() => {
          const url = `${chain.blockExplorers?.default.url}/tx/${receipt?.transactionHash}`;
          window.open(url, "_blank");
        }}
        chainId={chain.id}
        amount={transaction.amountOut}
        amountIn={transaction.amountIn}
        fromToken={transaction.from}
        isStakeWithHgEth={isStakeWithHgEth}
      />
    );
  }

  return (
    <ModalDrawer
      isOpen={true}
      closeAlert={closeModal}
      closeOnOverlayClick={false}
      maxH={isUnstakeTx ? "95%" : "75%"}
      bodyProps={{ overflow: "hidden" }}
    >
      <Flex
        direction="column"
        gap="24px"
        padding="24px 32px"
        width={{ base: "100%", md: "548px" }}
        maxWidth={{ base: "100%", md: "548px" }}
      >
        {!isUnstakeTx && (
          <Flex alignItems="center" justifyContent="space-between">
            {txModalTitle ? (
              txModalTitle
            ) : (
              <Text
                fontSize="16px"
                lineHeight="24px"
                fontWeight={500}
                color="#1A1A1A"
              >
                Review {TRANSACTIONS_KEYS[transactionType]}
              </Text>
            )}

            <IconButton
              icon={<IoClose size={16} color="#075A5A" weight="regular" />}
              aria-label="Close Icon"
              backgroundColor="transparent"
              _hover={{ backgroundColor: "transparent" }}
              onClick={closeModal}
              border="1px solid #EEEEEF"
              borderRadius="16px"
              className="!w-8 !h-auto !aspect-square"
            />
          </Flex>
        )}

        {isUnstakeTx ? (
          <UnstakeConfirmation closeModal={closeModal} action={nextAction} />
        ) : (
          <>
            <Box padding="0" borderRadius="1rem">
              <Flex
                justifyContent="center"
                flexDirection="column"
                alignItems="center"
                gap="8px"
                width="100%"
              >
                <Flex
                  justifyContent="space-between"
                  alignItems={{ base: "start", md: "center" }}
                  gap={{ base: "0.5rem", md: "1rem" }}
                  direction={{ base: "row", md: "row" }}
                  width="100%"
                >
                  <Flex
                    gap="6px"
                    justifyContent="center"
                    alignItems="center"
                    bg="#F4F4F7"
                    borderRadius="16px"
                    padding="24px"
                    width="50%"
                    minWidth="130px"
                    maxWidth="250px"
                  >
                    <Box width="max-content">
                      <BaseImage
                        src={LSTIconMapping[props.transaction.from]}
                        width={24}
                        height={24}
                        alt={props.transaction.from}
                      />
                    </Box>
                    <Flex
                      alignItems={{ base: "start", md: "center" }}
                      direction="column"
                      gap="2px"
                      width="max-content"
                    >
                      <Text
                        color="#4A6A6A"
                        fontSize="10px"
                        lineHeight="12px"
                        fontWeight="400"
                        align="left"
                      >
                        You pay
                      </Text>
                      <Text
                        fontSize="16px"
                        lineHeight="24px"
                        color="#1A1A1A"
                        fontWeight={500}
                        width="max-content"
                      >
                        {props.transaction.amountIn} {props.transaction.from}
                      </Text>
                    </Flex>
                  </Flex>
                  <Flex
                    gap="6px"
                    justifyContent="center"
                    alignItems="center"
                    bg="#F4F4F7"
                    borderRadius="16px"
                    padding="24px"
                    width="50%"
                    minWidth="130px"
                    maxWidth="250px"
                  >
                    <Box width="max-content">
                      <BaseImage
                        src={LSTIconMapping[props.transaction.to]}
                        width={24}
                        height={24}
                        alt={props.transaction.to}
                      />
                    </Box>
                    <Flex
                      alignItems={{ base: "start", md: "center" }}
                      direction="column"
                      gap="2px"
                      width="max-content"
                    >
                      <Text
                        color="#4A6A6A"
                        fontSize="10px"
                        lineHeight="12px"
                        fontWeight="400"
                        align="left"
                      >
                        You get
                      </Text>
                      <Text
                        fontSize="16px"
                        lineHeight="24px"
                        color="#1A1A1A"
                        fontWeight={500}
                        width="max-content"
                      >
                        {props.transaction.amountOut} {props.transaction.to}
                      </Text>
                    </Flex>
                  </Flex>
                </Flex>
                {isStakeWithHgEth &&
                  transaction.amountIn &&
                  transaction.amountOut && (
                    <Flex
                      justifyContent="space-between"
                      alignItems="center"
                      width="100%"
                      padding="0 4px"
                      marginBottom="4px"
                    >
                      <Text
                        fontSize="11px"
                        lineHeight="14px"
                        fontWeight={400}
                        color="#4A6A6A"
                      >
                        1 {gainVaultToken} = {gainVaultRate} ETH
                      </Text>
                      <Link
                        href={gainVaultLink}
                        passHref
                        legacyBehavior
                      >
                        <Text
                          as="a"
                          target="_blank"
                          rel="noopener noreferrer"
                          fontSize="11px"
                          lineHeight="14px"
                          fontWeight={500}
                          color="#075A5A"
                          textDecoration="underline"
                          cursor="pointer"
                        >
                          View {gainVaultToken} Vault
                        </Text>
                      </Link>
                    </Flex>
                  )}
                {transaction?.interactionPool && (
                  <Box
                    bg="#FFF4E6"
                    borderRadius="12px"
                    padding="12px 16px"
                    marginTop="12px"
                    width="100%"
                    border="1px solid #FFE5B4"
                  >
                    <Flex
                      gap="8px"
                      flexDirection="column"
                      alignItems="flex-start"
                    >
                      <Text
                        fontSize="12px"
                        lineHeight="12px"
                        fontWeight={400}
                        color="#4A6A6A"
                      >
                        Please ensure you are interacting with{" "}
                      </Text>
                      <Text
                        fontSize="12px"
                        lineHeight="16px"
                        fontWeight={600}
                        color="#1A1A1A"
                        wordBreak="break-all"
                      >
                        {chain.name}: {transaction.interactionPool}
                      </Text>
                    </Flex>
                  </Box>
                )}
              </Flex>
            </Box>

            {/* KSP:214 - Gain Opportunities with footer buttons*/}

            {props.type === TransactionType.APPROVAL && (
              <Stepper
                orientation="vertical"
                index={props.status === TransactionType.APPROVAL ? 0 : 1}
                height="120px"
                padding={{ base: "0", md: 0 }}
                gap={0}
                size="lg"
              >
                {steps.map((step, index) => {
                  const showError = props.error.approval && index === 0;
                  return (
                    <Step key={index}>
                      <StepIndicator
                        background={showError ? "#FF4D4D" : "white"}
                        borderColor={
                          showError ? "#FF4D4D !important" : "#B6DFDF"
                        }
                      >
                        <StepStatus
                          incomplete={<StepNumber />}
                          active={
                            showError ? (
                              <IoClose size={20} color="white" />
                            ) : (
                              <Spinner size="sm" color="#0089D2" />
                            )
                          }
                          complete={<FaCheck />}
                        />
                      </StepIndicator>
                      <Box flexShrink="0" pt="0.7rem">
                        <StepTitle>
                          <Flex gap="0.75rem" alignItems="center">
                            <Text
                              fontSize="14px"
                              lineHeight="18px"
                              fontWeight={400}
                              color="#1A1A1A"
                            >
                              {step.title}
                            </Text>
                            {showError && (
                              <Button
                                height="27px"
                                padding="6px 8px"
                                borderRadius="8px"
                                backgroundColor="#075A5A"
                                _hover={{
                                  backgroundColor: "#075A5A",
                                  color: "white",
                                }}
                                onClick={() => props?.retryTransaction?.()}
                                color="white"
                                leftIcon={
                                  <IoReloadOutline width={12} height={12} />
                                }
                                iconSpacing="4px"
                                fontSize={10}
                                lineHeight={12}
                                fontWeight={400}
                              >
                                Retry
                              </Button>
                            )}
                          </Flex>
                        </StepTitle>
                      </Box>
                      <StepSeparator />
                    </Step>
                  );
                })}
              </Stepper>
            )}

            {props.type === TransactionType.DEPOSIT &&
              (props.inProgress ? (
                <Flex gap="12px" alignItems="center">
                  <Flex
                    alignItems="center"
                    justifyContent="center"
                    border="1px solid #6E9BDE"
                    borderRadius="50%"
                    padding="8px"
                  >
                    <Spinner size="sm" color="#0089D2" />
                  </Flex>
                  <Text color="#075A5A">
                    Confirming {TRANSACTIONS_KEYS[transactionType]}
                  </Text>
                </Flex>
              ) : (
                <Button
                  width="12rem"
                  backgroundColor="#075A5A"
                  color="white"
                  _hover={{
                    backgroundColor: "#075A5A",
                    color: "white",
                  }}
                  alignSelf="center"
                  borderRadius="0.5rem"
                  onClick={onConfirm}
                >
                  Confirm {TRANSACTIONS_KEYS[transactionType]}
                </Button>
              ))}
          </>
        )}
      </Flex>
    </ModalDrawer>
  );
};

export default DepositModalV2;
